import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/config/api";
import { BRAND_LOGO_PATH, SAFARCORE_SITE_URL } from "@/utils/constants";
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  Users,
  UserPlus,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Settings,
  LogOut,
  Menu,
  X,
  UserCog,
  Activity,
  Building2,
  Wallet,
  ListChecks,
  Bell,
  Volume2,
  VolumeX,
} from "lucide-react";

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [companySettings, setCompanySettings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [bellRinging, setBellRinging] = useState(false);
  const soundPrefKey = `notif_sound_${user?.id || "guest"}`;
  const [soundEnabled, setSoundEnabled] = useState(true);
  const previousUnreadRef = useRef(0);

  useEffect(() => {
    api.settings
      .getCompany()
      .then((data) => setCompanySettings(data || null))
      .catch(() => setCompanySettings(null));
  }, []);

  const loadNotifications = async () => {
    try {
      const [summary, list] = await Promise.all([
        api.notifications.summary().catch(() => ({ unread_count: 0 })),
        api.notifications.list().catch(() => []),
      ]);
      setUnreadCount(Number(summary?.unread_count || 0));
      setNotifications(Array.isArray(list) ? list.slice(0, 8) : []);
    } catch (_) {
      setUnreadCount(0);
      setNotifications([]);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(soundPrefKey);
      setSoundEnabled(stored !== "off");
    } catch (_) {
      setSoundEnabled(true);
    }
  }, [soundPrefKey]);

  const playNotificationSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (_) {
      // Ignore browsers blocking audio autoplay.
    }
  };

  useEffect(() => {
    const previous = previousUnreadRef.current;
    if (unreadCount > previous) {
      setBellRinging(true);
      if (soundEnabled) playNotificationSound();
      const timer = setTimeout(() => setBellRinging(false), 1200);
      previousUnreadRef.current = unreadCount;
      return () => clearTimeout(timer);
    }
    previousUnreadRef.current = unreadCount;
  }, [unreadCount, soundEnabled]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const role = user?.role != null ? String(user.role).toLowerCase() : "";
  const parsedReportPermissions =
    typeof user?.report_permissions === "string"
      ? (() => {
          try {
            return JSON.parse(user.report_permissions);
          } catch (_) {
            return null;
          }
        })()
      : user?.report_permissions || null;
  const reportsMenuEnabled =
    role === "admin" ||
    (Boolean(parsedReportPermissions?.enabled) &&
      Array.isArray(parsedReportPermissions?.allowed_reports) &&
      parsedReportPermissions.allowed_reports.length > 0);
  // Super Admin menu only when backend has set is_super_admin (no email/domain fallback)
  const isSuperAdmin = user?.is_super_admin === true;

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    ...(role === "sales" || role === "admin"
      ? [{ icon: ShoppingCart, label: "Orders", path: "/orders" }]
      : []),
    { icon: FileText, label: "Invoices", path: "/invoices" },
    ...(role === "sales" || role === "admin"
      ? [{ icon: UserPlus, label: "Leads", path: "/leads" }]
      : []),
    { icon: Users, label: "Customers", path: "/customers" },
    { icon: DollarSign, label: "Payments", path: "/payments" },
    {
      icon: ListChecks,
      label: "Workflow Tickets",
      path: "/workflow-tickets",
    },
    ...(role === "finance" || role === "admin" || role === "management"
      ? [
          {
            icon: TrendingDown,
            label: "Income & Expenses",
            path: "/income-expenses",
          },
        ]
      : []),
    ...(reportsMenuEnabled
      ? [{ icon: TrendingUp, label: "Reports", path: "/reports" }]
      : []),
    ...(role === "admin" || role === "finance" || role === "management" || role === "sales"
      ? [{ icon: Building2, label: "Vendors", path: "/vendors" }]
      : []),
    ...(role === "admin" || role === "finance" || role === "management"
      ? [{ icon: Wallet, label: "Company account", path: "/company-account" }]
      : []),
    ...(role === "admin"
      ? [{ icon: UserCog, label: "Team", path: "/team" }]
      : []),
    ...(isSuperAdmin
      ? [{ icon: Activity, label: "Super Admin", path: "/super-admin" }]
      : []),
    ...(role === "admin"
      ? [{ icon: Settings, label: "Settings", path: "/settings" }]
      : []),
  ];

  const isActive = (path) => location.pathname === path;

  const companyLogoUrl = companySettings?.logo_url?.trim();
  const hasCompanyLogo = Boolean(companyLogoUrl);

  const handleNotificationClick = async (n) => {
    try {
      if (!n.is_read) await api.notifications.markRead(n.id);
      if (n.entity_type === "workflow_ticket" && n.entity_id) {
        navigate(`/workflow-tickets?focusTicket=${encodeURIComponent(n.entity_id)}`);
      }
      loadNotifications();
      setShowNotifications(false);
    } catch (_) {}
  };

  return (
    <div className="flex h-screen bg-stone-100">
      {/* Sidebar — Deep Navy; brand mark + optional company name */}
      <aside
        className={`bg-secondary text-white transition-all duration-300 flex flex-col shrink-0 ${
          sidebarOpen ? "w-52 lg:w-48" : "w-16"
        }`}
      >
        <div
          className={`border-b border-white/10 shrink-0 ${sidebarOpen ? "px-4 py-3" : "px-2 py-3"}`}
        >
          <div
            className={`flex items-center ${sidebarOpen ? "justify-between gap-2" : "justify-between w-full"}`}
          >
            {sidebarOpen ? (
              <div className="min-w-0 flex-1">
                <div className="min-w-0 max-w-[200px]">
                  <img
                    src={hasCompanyLogo ? companyLogoUrl : BRAND_LOGO_PATH}
                    alt={
                      companySettings?.name ||
                      (hasCompanyLogo ? "Company logo" : "SafarCore")
                    }
                    className="max-h-[72px] w-full h-auto object-contain object-left"
                  />
                </div>
                {companySettings?.name && (
                  <p className="text-[10px] text-white/70 truncate mt-1">
                    {companySettings.name}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center shrink-0 min-h-[32px] min-w-[32px]">
                <img
                  src={hasCompanyLogo ? companyLogoUrl : BRAND_LOGO_PATH}
                  alt=""
                  className="h-8 w-8 min-h-[32px] min-w-[32px] object-contain rounded"
                  title={
                    hasCompanyLogo
                      ? companySettings?.name || "Company"
                      : "SafarCore"
                  }
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors shrink-0"
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={!sidebarOpen ? item.label : undefined}
                className={`flex items-center rounded-lg transition-all ${
                  sidebarOpen
                    ? "gap-2.5 px-3 py-2.5 mx-2"
                    : "justify-center p-2.5 mx-1.5"
                } ${
                  isActive(item.path)
                    ? "text-white shadow-[inset_3px_0_0_#48CAE4] bg-white/10"
                    : "hover:bg-white/10 text-white/80"
                }`}
              >
                <Icon size={18} className="shrink-0" />
                {sidebarOpen && (
                  <span className="text-sm font-medium truncate">
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div
          className={`border-t border-white/10 shrink-0 ${sidebarOpen ? "p-3" : "p-2"}`}
        >
          <div
            className={`flex items-center ${sidebarOpen ? "gap-2.5 px-3 py-2 mb-2" : "justify-center py-2"}`}
          >
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-xs font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{user?.name}</p>
                <p className="text-[10px] text-white/60 truncate">
                  {user?.role}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            title={!sidebarOpen ? "Logout" : undefined}
            className={`w-full flex items-center rounded-lg hover:bg-white/10 text-white/80 transition-colors ${
              sidebarOpen ? "gap-2.5 px-3 py-2.5" : "justify-center p-2.5"
            }`}
          >
            <LogOut size={18} className="shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
          {sidebarOpen && (
            <div className="pt-2 mt-1">
              <p className="text-[10px] text-white/50 text-center leading-tight">
                {/* Powered by{" "} */}
                <a
                  href={SAFARCORE_SITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary/90 hover:text-primary underline-offset-2 hover:underline"
                >
                  safarcore.com
                </a>
              </p>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content: white background for outlet */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden overflow-x-hidden bg-white">
        <header className="flex-shrink-0 border-b border-stone-100 z-40 bg-white min-w-0 shadow-nav">
          <div className="px-4 sm:px-6 py-4 min-w-0 flex items-center justify-between gap-3">
            <h2 className="text-xl sm:text-2xl font-heading font-semibold text-secondary truncate">
              {menuItems.find((item) => isActive(item.path))?.label ||
                "Dashboard"}
            </h2>
            <div className="relative flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const next = !soundEnabled;
                  setSoundEnabled(next);
                  try {
                    localStorage.setItem(soundPrefKey, next ? "on" : "off");
                  } catch (_) {}
                }}
                className="p-2 rounded-lg border border-stone-200 hover:bg-stone-50"
                title={soundEnabled ? "Mute notification sound" : "Enable notification sound"}
              >
                {soundEnabled ? (
                  <Volume2 size={16} className="text-stone-700" />
                ) : (
                  <VolumeX size={16} className="text-stone-700" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowNotifications((v) => !v)}
                className="relative p-2 rounded-lg border border-stone-200 hover:bg-stone-50"
                title="Notifications"
              >
                <Bell
                  size={18}
                  className={`text-stone-700 ${bellRinging ? "bell-ringing" : ""}`}
                />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-11 w-96 max-w-[92vw] bg-white border border-stone-200 rounded-xl shadow-lg z-50">
                  <div className="px-3 py-2 border-b border-stone-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-stone-800">Notifications</p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={async () => {
                          await api.notifications.markAllRead().catch(() => {});
                          loadNotifications();
                        }}
                      >
                        Mark all read
                      </button>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="px-3 py-4 text-xs text-stone-500">No notifications yet.</p>
                    ) : (
                      notifications.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => handleNotificationClick(n)}
                          className={`w-full text-left px-3 py-2 border-b border-stone-100 hover:bg-stone-50 ${
                            n.is_read ? "opacity-80" : ""
                          }`}
                        >
                          <p className="text-sm font-medium text-stone-800">{n.title}</p>
                          {n.message && <p className="text-xs text-stone-600 mt-0.5">{n.message}</p>}
                          <p className="text-[11px] text-stone-500 mt-1">
                            {new Date(n.created_at).toLocaleString()}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
              <a
                href={SAFARCORE_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:block shrink-0 opacity-90 hover:opacity-100 transition-opacity"
                title="SafarCore"
              >
                <img
                  src={BRAND_LOGO_PATH}
                  alt="SafarCore"
                  className="h-8 w-auto object-contain"
                />
              </a>
            </div>
          </div>
        </header>

        <main className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 md:p-8 lg:p-12 bg-white">
          <div className="min-w-0 max-w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};
